import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Html5Qrcode } from 'html5-qrcode';
import { stampsAPI } from '../services/api';
import SearchIcon from '@mui/icons-material/Search';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import './Verify.css';

const QR_ELEMENT_ID = 'qr-reader';

function Verify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [stamp, setStamp] = useState(null);
  // Start as true if uid param is present — skips showing search page flash
  const uid = searchParams.get('uid');
  const [loading, setLoading] = useState(!!uid);
  const [manualUid, setManualUid] = useState('');
  const [showQRScanner, setShowQRScanner] = useState(false);
  const html5QrRef = useRef(null);
  const scannerActiveRef = useRef(false);

  // Auto-fetch when uid is in URL (e.g. from QR code scan via phone camera)
  useEffect(() => {
    if (uid) fetchStampData(uid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  // Start QR scanner when showQRScanner becomes true
  useEffect(() => {
    if (!showQRScanner) return;

    const scanner = new Html5Qrcode(QR_ELEMENT_ID);
    html5QrRef.current = scanner;
    scannerActiveRef.current = true;

    scanner.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: { width: 250, height: 250 } },
      (decodedText) => {
        stopScanner();
        try {
          const url = new URL(decodedText);
          const uidParam = url.searchParams.get('uid');
          if (uidParam) {
            fetchStampData(uidParam);
          } else {
            fetchStampData(decodedText.trim());
          }
        } catch {
          fetchStampData(decodedText.trim());
        }
      },
      () => {}
    ).catch((err) => {
      toast.error('Unable to access camera: ' + err);
      setShowQRScanner(false);
    });

    return () => { stopScanner(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showQRScanner]);

  const stopScanner = () => {
    if (html5QrRef.current && scannerActiveRef.current) {
      scannerActiveRef.current = false;
      html5QrRef.current.stop().catch(() => {}).finally(() => {
        html5QrRef.current = null;
        setShowQRScanner(false);
      });
    } else {
      setShowQRScanner(false);
    }
  };

  const fetchStampData = async (stampUid) => {
    try {
      setLoading(true);
      const response = await stampsAPI.verify(stampUid);
      setStamp(response.data.stamp);
    } catch {
      toast.error('Stamp not found or invalid QR code');
      setStamp(null);
    } finally {
      setLoading(false);
    }
  };

  const handleManualSearch = (e) => {
    e.preventDefault();
    if (manualUid.trim()) { fetchStampData(manualUid.trim()); setManualUid(''); }
  };

  const getInfoRows = (stampData) => {
    if (stampData.info_rows) {
      try {
        const parsed = typeof stampData.info_rows === 'string'
          ? JSON.parse(stampData.info_rows)
          : stampData.info_rows;
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch {}
    }
    const rows = [];
    let i = 1;
    while (stampData[`info${i}_name`] !== undefined) {
      rows.push({
        type: stampData[`info${i}_type`] || '—',
        name: stampData[`info${i}_name`] || '—',
        cnic: stampData[`info${i}_cnic`] || '—',
        contact: stampData[`info${i}_contact`] || '—',
        use_on_form: stampData[`info${i}_use_on_form`] || '—',
      });
      i++;
    }
    return rows;
  };

  const formatStatus = (status) => {
    if (!status) return '';
    const s = status.toLowerCase();
    if (s === 'verified') return 'Issued';
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const formatChallanType = (type) => {
    if (!type) return '';
    if (type.toLowerCase() === 'non-judicial') return 'Original';
    return type;
  };

  const Footer = () => (
    <footer className="vp-footer">
      ©2026 , Powered by <b className="vp-footer-brand">KPITB</b>
    </footer>
  );

  // ── Loading state (when uid param present — no flash of search page) ──
  if (loading) {
    return (
      <div className="vp-page">
        <div className="vp-search-wrap">
          <div className="vp-search-card" style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ color: 'rgb(81,99,119)', fontFamily: 'Rubik, sans-serif', fontSize: '1rem' }}>
              Loading stamp details...
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ── Search / landing ──
  if (!stamp && !showQRScanner) {
    return (
      <div className="vp-page">
        <div className="vp-search-wrap">
          <div className="vp-search-card">
            <div className="vp-search-logo">
              <i className="fa fa-print" style={{ color: 'rgb(81,99,119)', fontSize: '1.15rem' }} />
              Stamp Verification System
            </div>
            <p className="vp-search-sub">Enter a Stamp ID or scan a QR code to verify</p>
            <form onSubmit={handleManualSearch} className="vp-search-form">
              <div className="vp-search-input-wrap">
                <SearchIcon className="vp-search-icon" />
                <input
                  type="text"
                  placeholder="Enter Stamp ID (e.g. KP-PSW-XXXXXXXXX)"
                  value={manualUid}
                  onChange={(e) => setManualUid(e.target.value)}
                  required
                />
              </div>
              <button type="submit" className="vp-btn vp-btn-primary" disabled={loading}>
                {loading ? 'Searching...' : 'Verify Stamp'}
              </button>
            </form>
            <div className="vp-divider"><span>OR</span></div>
            <button onClick={() => setShowQRScanner(true)} className="vp-btn vp-btn-outline">
              <QrCodeScannerIcon fontSize="small" /> Scan QR Code
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ── QR Scanner ──
  if (showQRScanner) {
    return (
      <div className="vp-page">
        <div className="vp-search-wrap">
          <div className="vp-search-card">
            <h3 style={{ marginTop: 0, color: 'rgb(81,99,119)', fontFamily: 'Rubik, serif' }}>
              Point Camera at QR Code
            </h3>
            <div id={QR_ELEMENT_ID} style={{ width: '100%' }} />
            <button onClick={stopScanner} className="vp-btn vp-btn-danger" style={{ marginTop: 12 }}>
              Close Scanner
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const infoRows = getInfoRows(stamp);

  return (
    <div className="vp-page vp-page-result">
      <div className="vp-result-wrap">
        <div className="vp-card">
          <div className="vp-card-body">

            {/* Stamp Details */}
            <h5 className="vp-section-heading">
              <i className="fa fa-print" style={{ marginTop: '-5px' }} />
              Stamp Details
            </h5>
            <div className="row g-3">
              <table className="vp-basic-table">
                <tbody>
                  <tr>
                    <td className="TD"><b>Stamp Number</b></td>
                    <td className="TD">{stamp.stamp_number}</td>
                    <td className="TD"><b>Stamp Status</b></td>
                    <td className="TD">
                      <button className="vp-btn-success vp-btn-sm">
                        {formatStatus(stamp.stamp_status)}
                      </button>
                    </td>
                  </tr>
                  <tr>
                    <td className="TD"><b>Total Stamp Amount (Rs)</b></td>
                    <td className="TD">{stamp.stamp_amount}</td>
                    <td className="TD"><b>Total Paid Amount</b></td>
                    <td className="TD">{stamp.total_paid_amount}</td>
                  </tr>
                </tbody>
              </table>

              <table className="table table-bordered table-responsive vp-challan-table" id="deedTable">
                <thead>
                  <tr>
                    <th>Challan Number</th>
                    <th>Type</th>
                    <th>Branch Code</th>
                    <th>Payment Reference</th>
                    <th>Payment Date</th>
                    <th>Stamp Amount</th>
                    <th>Stamp S.No</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{stamp.challan_number}</td>
                    <td>
                      <button className="vp-btn-success vp-btn-sm vp-btn-block">
                        {formatChallanType(stamp.challan_type)}
                      </button>
                    </td>
                    <td>{stamp.branch_code}</td>
                    <td>{stamp.payment_reference}</td>
                    <td>{stamp.payment_date}</td>
                    <td>{stamp.stamp_amount}</td>
                    <td>{stamp.stamp_sno}{stamp.vendor_stamp ? <><br /><small>Vendor Stamp</small></> : null}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <hr className="vp-hr" />

            {/* Deed Detail */}
            <h5 className="vp-section-heading">
              <i className="fa fa-handshake" style={{ marginTop: '-5px' }} />
              Deed Detail
            </h5>
            <div className="row g-3">
              <table className="vp-basic-table">
                <tbody>
                  <tr>
                    <td className="TD"><b>Stamp Paper Type</b></td>
                    <td className="TD">{stamp.stamp_paper_type}</td>
                    <td className="TD"><b>Deed Type</b></td>
                    <td className="TD">{stamp.deed_type || ''}</td>
                  </tr>
                  <tr>
                    <td className="TD"><b>Challan Amount Paid By</b></td>
                    <td className="TD">{stamp.challan_amount_paid_by}</td>
                    <td className="TD"><b></b></td>
                    <td className="TD"></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <hr className="vp-hr" />

            {/* Challan Information */}
            <h5 className="vp-section-heading">
              <i className="bx bx-file" style={{ marginTop: '-5px' }} />
              Challan Information
            </h5>
            <div className="row g-3">
              <table className="vp-basic-table">
                <tbody>
                  <tr>
                    <td className="TD"><b>District</b></td>
                    <td className="TD">{stamp.district}</td>
                    <td className="TD"><b>Tehsil</b></td>
                    <td className="TD">{stamp.tehsil}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <hr className="vp-hr" />

            {/* Applicant Information */}
            <h5 className="vp-section-heading">
              <i className="fa fa-user" style={{ marginTop: '-5px' }} />
              Applicant Information
            </h5>
            <div className="row g-3">
              <table className="vp-basic-table">
                <tbody>
                  <tr>
                    <td className="TD"><b>Name</b></td>
                    <td className="TD">{stamp.applicant_name}</td>
                    <td className="TD"><b>CNIC</b></td>
                    <td className="TD">{stamp.applicant_cnic}</td>
                  </tr>
                  <tr>
                    <td className="TD"><b>Contact</b></td>
                    <td className="TD">{stamp.applicant_contact}</td>
                    <td className="TD"><b>Email</b></td>
                    <td className="TD">{stamp.applicant_email || ''}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <hr className="vp-hr" />

            {/* Information rows */}
            {infoRows.map((row, idx) => (
              <div key={idx}>
                <div className="row my-3">
                  <div className="col-md-6">
                    <h5 className="vp-section-heading">
                      <i className="bx bx-user-check" style={{ marginTop: '-5px' }} />
                      Information
                    </h5>
                  </div>
                  <div className="row">
                    <div className="col-md-12">
                      <div className="vp-info-card">
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th>TYPE</th>
                              <th>NAME / COMPANY NAME</th>
                              <th>CNIC / NTN</th>
                              <th>Contact #</th>
                              <th>Use On Form</th>
                            </tr>
                          </thead>
                          <tbody className="table-border-bottom-0">
                            <tr>
                              <td>{row.type}</td>
                              <td>{row.name}</td>
                              <td>{row.cnic}</td>
                              <td>{row.contact}</td>
                              <td>{row.use_on_form}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
                {idx < infoRows.length - 1 && <hr className="vp-hr" />}
              </div>
            ))}

            {/* HOME → navigates to 404 as client requested */}
            <div className="vp-home-wrap no-print">
              <button onClick={() => navigate('/home')} className="vp-btn-success">
                HOME
              </button>
            </div>

          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default Verify;
